// eslint-disable-next-line jira/restricted/react
import React, { PureComponent } from 'react';
import type { Store } from 'redux';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import { ViewTracker } from '@atlassian/jira-analytics-web-react';
import AppBase from '@atlassian/jira-app-base';
import AppStyle from '@atlassian/jira-common-components-app-style';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales';
import { ReportErrors } from '@atlassian/jira-errors-handling';
import { FlagsDispatcher } from '@atlassian/jira-flags';
import { initialQuery } from '@atlassian/jira-project-directories-components/src/query-param-adapter/model/constants/initial-query';
import type { RequestResult } from '@atlassian/jira-project-directories-components/src/state/requests/retrieve';
import { FETCH_COMPONENTS_REASON } from './common/constants/fetch-components-reason';
import flagsMapper from './flags';
import rootEpic from './ops';
import type { Query } from './query-param-adapter/model';
import createStore from './state';
import { getComponentsPageRequest, setComponentsPage } from './state/actions';
import { set as setAnalyticsData } from './state/analytics/actions';
import { set as setContext } from './state/context/actions';
import { set as setProject } from './state/project/actions';
import type { Project } from './state/project/types';
import { setQuery } from './state/query/actions';
import { getCurrentPage, getQuery } from './state/query/selectors';
import type { State } from './state/reducer';
import View from './view';

export type Props = {
	baseUrl: string;
	locale: Locale;
	projectKey: string;
	projectId: number;
	projectName: string;
	projectType: ProjectType;
	isAdmin: boolean;
	prefetchedData: RequestResult | null;
	onUnmount: () => void;
	query: Query;
	onQueryChanged: (query: Query) => void;
};

// eslint-disable-next-line jira/react/no-class-components
export default class ProjectComponentsApp extends PureComponent<Props> {
	static defaultProps = {
		isAdmin: false,
		prefetchedData: null,
		onUnmount: noop,
		query: initialQuery,
		onQueryChanged: noop,
	};

	constructor(props: Props) {
		super(props);

		const {
			locale,
			baseUrl,
			projectKey,
			projectId,
			projectName,
			isAdmin,
			projectType,
			prefetchedData,
			query,
			onQueryChanged,
		} = props;

		const project: Project = {
			id: projectId,
			key: projectKey,
			name: projectName,
			type: projectType,
		};

		const analyticsData = {
			projectType,
		};

		this.store = createStore(rootEpic);
		this.store.dispatch(setContext({ baseUrl, isAdmin, locale, onQueryChanged }));
		this.store.dispatch(setProject(project));
		this.store.dispatch(setAnalyticsData(analyticsData));
		this.store.dispatch(setQuery(query || initialQuery));

		if (prefetchedData) {
			this.store.dispatch(setComponentsPage(prefetchedData));
		} else {
			this.store.dispatch(
				getComponentsPageRequest({
					page: getCurrentPage(this.store.getState()),
					initiatedBy: FETCH_COMPONENTS_REASON.VIEW,
				}),
			);
		}
	}

	componentDidUpdate() {
		const { query } = this.props;
		if (query && query && !isEqual(query, getQuery(this.store.getState()))) {
			this.store.dispatch(setQuery(query));
		}
	}

	componentWillUnmount() {
		this.props.onUnmount();
	}

	store: Store<State>;

	render() {
		return (
			<AppBase store={this.store} hasErrorBoundary={false}>
				<ReportErrors
					id="unhandled"
					packageName="jiraProjectDirectoriesComponents"
					teamName="morphinecake"
					sendToPrivacyUnsafeSplunk
				>
					<AppStyle>
						<FlagsDispatcher mapper={flagsMapper} />
						<View />
						<ViewTracker />
					</AppStyle>
				</ReportErrors>
			</AppBase>
		);
	}
}
